/**
 * Bandholz Theme Customizations
 *
 * Global JavaScript customizations layered on top of the Ritual theme.
 * Keep custom behavior isolated from Ritual's original theme scripts.
 *
 * Current customizations:
 * 1. Move custom product-card badges into Ritual card galleries.
 * 2. Copy Ritual's native badge wrapper settings to custom badges.
 * 3. Replace the Header bundle product price with a fixed savings message.
 * 4. Replace Ritual's Header account and cart artwork with Bandholz pixel icons.
 * 5. Restore native list semantics to Ritual's desktop Header navigation.
 * 6. Add accessible names to Ritual's sticky Add to Cart buttons.
 * 7. Repair invalid Judge.me media-grid selection semantics.
 * 8. Extend the cart drawer's native modal-overlay behavior through 1252px.
 * 9. Show Shopify-hosted pixel hearts on Judge.me read-only review ratings.
 *
 * Bundle product:
 * • Shopify product ID: 8437895200959
 * • Handle fallback: custom-fragrance-set
 *
 * Important:
 * • Do not copy Ritual's native scripts into this file.
 * • Use this file only for Bandholz-specific progressive enhancements.
 * • This file is globally loaded with the defer attribute.
 *
 * -Jenny 9/24/26
 */

(() => {
  'use strict';

  /**
   * Read the configuration rendered by
   * snippets/bandholz-customizations-assets.liquid.
   *
   * @returns {{
   *   bundleProductId: string,
   *   bundleProductHandle: string,
   *   bundleCardMessage: string
   * }}
   */
  function getBandholzConfig() {
    const configElement = document.querySelector(
      '#bandholz-customizations-config'
    );

    const fallbackConfig = {
      bundleProductId: '8437895200959',
      bundleProductHandle: 'custom-fragrance-set',
      bundleCardMessage: 'SAVE UP TO 25% ON SETS'
    };

    if (!configElement) {
      return fallbackConfig;
    }

    try {
      const parsedConfig = JSON.parse(
        configElement.textContent || '{}'
      );

      return {
        bundleProductId:
          String(parsedConfig.bundleProductId || '') ||
          fallbackConfig.bundleProductId,

        bundleProductHandle:
          parsedConfig.bundleProductHandle ||
          fallbackConfig.bundleProductHandle,

        bundleCardMessage:
          parsedConfig.bundleCardMessage ||
          fallbackConfig.bundleCardMessage
      };
    } catch (error) {
      console.warn(
        'Bandholz customizations: unable to read configuration.',
        error
      );

      return fallbackConfig;
    }
  }

  const config = getBandholzConfig();


  /* --------------------------------------------------------------------------
   * 8. Cart drawer modal-overlay extension
   *
   * Ritual 4.2.0 opens theme drawers as true modal overlays below 990px and
   * switches them to non-modal page-squeeze drawers at 990px and wider.
   *
   * For the cart only, extend Ritual's existing modal-overlay behavior through
   * 1252px so normal narrow-desktop widths do not compress the storefront.
   * At 1253px and wider, control returns to Ritual's normal squeeze mode.
   *
   * This enhancement:
   * • Uses Ritual's existing <dialog>, focus and scroll-lock utilities.
   * • Leaves Ritual's theme-drawer source files untouched.
   * • Does not replace the drawer component or its open/close methods.
   * • Does not change chat or other theme drawers.
   * • Does not alter PDP layout or responsive breakpoints.
   *
   * -Jenny 9/22/26
   * -------------------------------------------------------------------------- */

  const BANDHOLZ_CART_OVERLAY_QUERY = window.matchMedia(
    '(min-width: 990px) and (max-width: 1252px)'
  );

  let bandholzDrawerHelpersPromise;

  /**
   * Resolve Ritual's existing module URLs from its import map, then reuse the
   * same focus and scroll-lock helpers used by theme-drawer.js.
   *
   * @returns {Promise<{
   *   lockScroll: Function,
   *   unlockScroll: Function,
   *   trapFocus: Function,
   *   removeTrapFocus: Function
   * } | null>}
   */
  function getBandholzDrawerHelpers() {
    if (bandholzDrawerHelpersPromise) {
      return bandholzDrawerHelpersPromise;
    }

    const importMapElement = document.querySelector(
      'script[type="importmap"]'
    );

    if (!(importMapElement instanceof HTMLScriptElement)) {
      return Promise.resolve(null);
    }

    try {
      const imports = JSON.parse(
        importMapElement.textContent || '{}'
      ).imports || {};

      const utilitiesUrl = imports['@theme/utilities'];
      const focusUrl = imports['@theme/focus'];

      if (!utilitiesUrl || !focusUrl) {
        return Promise.resolve(null);
      }

      bandholzDrawerHelpersPromise = Promise.all([
        import(utilitiesUrl),
        import(focusUrl)
      ])
        .then(([utilities, focus]) => ({
          lockScroll: utilities.lockScroll,
          unlockScroll: utilities.unlockScroll,
          trapFocus: focus.trapFocus,
          removeTrapFocus: focus.removeTrapFocus
        }))
        .catch((error) => {
          console.warn(
            'Bandholz customizations: unable to load Ritual drawer helpers.',
            error
          );

          return null;
        });

      return bandholzDrawerHelpersPromise;
    } catch (error) {
      console.warn(
        'Bandholz customizations: unable to read Ritual import map.',
        error
      );

      return Promise.resolve(null);
    }
  }

  /**
   * Keep focus inside the cart when switching between Ritual's modal and
   * squeeze dialog modes without replaying the open animation.
   *
   * @param {HTMLDialogElement} panel
   * @param {Element|null} previousActiveElement
   */
  function restoreCartDrawerFocus(
    panel,
    previousActiveElement
  ) {
    const focusTarget =
      previousActiveElement instanceof HTMLElement &&
      panel.contains(previousActiveElement)
        ? previousActiveElement
        : panel.querySelector(
            '.theme-drawer__close-button'
          );

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({
        preventScroll: true
      });
    }
  }

  /**
   * Synchronize only the cart drawer with the Bandholz 1252px handoff.
   *
   * Ritual itself still owns the drawer element, animation, Escape handling,
   * backdrop click handling and close lifecycle.
   */
  async function syncBandholzCartDrawerMode() {
    const cartDrawer =
      document.getElementById('cart-drawer');

    const panel =
      cartDrawer?.querySelector(
        'dialog[ref="panel"]'
      );

    if (
      !(cartDrawer instanceof HTMLElement) ||
      !(panel instanceof HTMLDialogElement) ||
      !cartDrawer.hasAttribute('open')
    ) {
      return;
    }

    const helpers =
      await getBandholzDrawerHelpers();

    if (!helpers) {
      return;
    }

    const pageWrapper =
      document.querySelector('.page-wrapper');

    /*
     * 990px–1252px:
     * Ritual has already opened the cart as its desktop non-modal drawer.
     * Switch that same dialog into the true modal mode Ritual uses below 990px.
     */
    if (BANDHOLZ_CART_OVERLAY_QUERY.matches) {
      if (
        panel.open &&
        !panel.matches(':modal')
      ) {
        const previousActiveElement =
          document.activeElement;

        panel.close();
        helpers.removeTrapFocus();
        helpers.lockScroll(panel);
        panel.showModal();

        restoreCartDrawerFocus(
          panel,
          previousActiveElement
        );
      }

      cartDrawer.dataset.bandholzCartOverlay =
        'true';

      pageWrapper?.classList.remove(
        'page-wrapper--drawer-open'
      );

      return;
    }

    /*
     * 1253px+:
     * If this cart was using the Bandholz extended modal range, return the
     * same dialog to Ritual's normal desktop non-modal squeeze mode.
     */
    if (
      window.innerWidth >= 1253 &&
      cartDrawer.dataset
        .bandholzCartOverlay === 'true'
    ) {
      if (
        panel.open &&
        panel.matches(':modal')
      ) {
        const previousActiveElement =
          document.activeElement;

        panel.close();
        helpers.removeTrapFocus();
        helpers.unlockScroll(panel);
        panel.show();
        helpers.trapFocus(panel);

        restoreCartDrawerFocus(
          panel,
          previousActiveElement
        );
      }

      delete cartDrawer.dataset
        .bandholzCartOverlay;

      pageWrapper?.classList.add(
        'page-wrapper--drawer-open'
      );

      return;
    }

    /*
     * Below 990px Ritual already owns its native modal mode. Remove only the
     * Bandholz marker and let the theme's own breakpoint handler take over.
     */
    if (window.innerWidth < 990) {
      delete cartDrawer.dataset
        .bandholzCartOverlay;

      pageWrapper?.classList.remove(
        'page-wrapper--drawer-open'
      );
    }
  }

  /*
   * theme-drawer:open bubbles after Ritual has completed its normal open
   * routine. Defer our synchronization to a microtask so Ritual's own global
   * squeeze coordinator finishes first, then apply the cart-only handoff.
   */
  document.addEventListener(
    'theme-drawer:open',
    (event) => {
      if (
        event.target instanceof Element &&
        event.target.id === 'cart-drawer'
      ) {
        queueMicrotask(
          syncBandholzCartDrawerMode
        );
      }
    }
  );

  document.addEventListener(
    'theme-drawer:close',
    (event) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.id === 'cart-drawer'
      ) {
        delete event.target.dataset
          .bandholzCartOverlay;
      }
    }
  );

  /*
   * If the shopper resizes while the cart is open, switch the same dialog
   * between the extended overlay range and Ritual's native squeeze mode.
   */
  BANDHOLZ_CART_OVERLAY_QUERY.addEventListener(
    'change',
    () => {
      queueMicrotask(
        syncBandholzCartDrawerMode
      );
    }
  );

  /*
   * Also reconcile a cart restored by Ritual's persisted-drawer system after
   * its custom element has finished upgrading.
   */
  customElements
    .whenDefined('theme-drawer')
    .then(() => {
      queueMicrotask(
        syncBandholzCartDrawerMode
      );
    });

  /**
   * Find the position class used by a Ritual product-badge wrapper.
   *
   * Example:
   * product-badges--top-right
   *
   * @param {HTMLElement} wrapper
   * @returns {string}
   */
  function getBadgePositionClass(wrapper) {
    return (
      Array.from(wrapper.classList).find((className) =>
        className.startsWith('product-badges--')
      ) || ''
    );
  }

  /**
   * Find a Ritual badge wrapper that can be used as the style source.
   *
   * Preference order:
   * 1. Native badge wrapper inside the current card gallery
   * 2. Another native product-card badge wrapper elsewhere on the page
   *
   * The fallback allows a custom badge on a full-price product to inherit the
   * active badge Theme settings even when that particular card has no native
   * SALE or SOLD OUT badge.
   *
   * @param {HTMLElement} gallery
   * @returns {HTMLElement|null}
   */
  function findNativeBadgeStyleSource(gallery) {
    const localWrapper = gallery.querySelector(
      ':scope > .product-badges:not(.bandholz-product-badges)'
    );

    if (localWrapper instanceof HTMLElement) {
      return localWrapper;
    }

    const pageWrapper = document.querySelector(
      [
        'product-card .card-gallery',
        'product-card [ref="cardGallery"]'
      ].join(', ')
    );

    if (pageWrapper instanceof HTMLElement) {
      const fallbackWrapper = pageWrapper.querySelector(
        ':scope > .product-badges:not(.bandholz-product-badges)'
      );

      if (fallbackWrapper instanceof HTMLElement) {
        return fallbackWrapper;
      }
    }

    const anyNativeWrapper = document.querySelector(
      '.product-badges:not(.bandholz-product-badges)'
    );

    return anyNativeWrapper instanceof HTMLElement
      ? anyNativeWrapper
      : null;
  }

  /**
   * Build a product-badge wrapper matching Ritual's native badge structure.
   *
   * Ritual stores the merchant-selected badge presentation on its wrapper
   * through inline CSS custom properties, including:
   * • Border radius
   * • Font family
   * • Font weight
   * • Text transform
   * • Position
   *
   * Copying the native style attribute allows the Bandholz badge to continue
   * following current and future Theme settings.
   *
   * @param {HTMLElement} badge
   * @param {string} fallbackBadgePosition
   * @param {HTMLElement|null} nativeBadgeWrapper
   * @returns {HTMLDivElement}
   */
  function createProductBadgeWrapper(
    badge,
    fallbackBadgePosition,
    nativeBadgeWrapper = null
  ) {
    const wrapper = document.createElement('div');

    const fallbackPositionClass =
      `product-badges--${fallbackBadgePosition || 'top-right'}`;

    wrapper.className = [
      'product-badges',
      fallbackPositionClass,
      'bandholz-product-badges'
    ].join(' ');

    if (nativeBadgeWrapper instanceof HTMLElement) {
      const nativeStyle =
        nativeBadgeWrapper.getAttribute('style');

      if (nativeStyle) {
        wrapper.setAttribute('style', nativeStyle);
      }

      const nativePositionClass =
        getBadgePositionClass(nativeBadgeWrapper);

      if (nativePositionClass) {
        wrapper.classList.remove(fallbackPositionClass);
        wrapper.classList.add(nativePositionClass);
      }
    }

    wrapper.appendChild(badge);

    return wrapper;
  }

  /**
   * Move server-rendered Bandholz custom badges into their card galleries.
   *
   * SOLD OUT remains controlled by Ritual. The Custom Liquid block only
   * renders a custom badge when the product is available.
   *
   * @param {Document|Element} root
   */
  function initializeProductCardBadges(root = document) {
    const markers = [];

    if (
      root instanceof Element &&
      root.matches('.bandholz-product-card-customizations')
    ) {
      markers.push(root);
    }

    if ('querySelectorAll' in root) {
      markers.push(
        ...root.querySelectorAll(
          '.bandholz-product-card-customizations'
        )
      );
    }

    for (const marker of markers) {
      if (!(marker instanceof HTMLElement)) {
        continue;
      }

      const productCard = marker.closest('product-card');

      const badge = marker.querySelector(
        '.bandholz-product-card-custom-badge'
      );

      if (
        !(productCard instanceof HTMLElement) ||
        !(badge instanceof HTMLElement)
      ) {
        continue;
      }

      if (
        badge.dataset.bandholzBadgeInitialized === 'true'
      ) {
        continue;
      }

      const gallery =
        productCard.querySelector('[ref="cardGallery"]') ||
        productCard.querySelector('.card-gallery');

      if (!(gallery instanceof HTMLElement)) {
        continue;
      }

      const nativeBadgeWrappers = Array.from(
        gallery.querySelectorAll(
          ':scope > .product-badges:not(.bandholz-product-badges)'
        )
      ).filter(
        (wrapper) => wrapper instanceof HTMLElement
      );

      const nativeBadgeStyleSource =
        nativeBadgeWrappers[0] ||
        findNativeBadgeStyleSource(gallery);

      /*
       * Hide only the native badge wrappers in the same card.
       *
       * The Custom Liquid block is only rendered for available products, so
       * these wrappers would represent a native SALE badge rather than the
       * higher-priority SOLD OUT badge.
       */
      for (const nativeWrapper of nativeBadgeWrappers) {
        nativeWrapper.setAttribute('hidden', '');
      }

      const fallbackBadgePosition =
        marker.dataset.bandholzBadgePosition ||
        'top-right';

      const customBadgeWrapper =
        createProductBadgeWrapper(
          badge,
          fallbackBadgePosition,
          nativeBadgeStyleSource
        );

      gallery.appendChild(customBadgeWrapper);

      badge.dataset.bandholzBadgeInitialized = 'true';
    }
  }

  /**
   * Read a possible Shopify product ID from a resource card.
   *
   * Different Ritual versions may expose it on the resource card itself or
   * on a nested product-related element.
   *
   * @param {HTMLElement} card
   * @returns {string}
   */
  function getResourceCardProductId(card) {
    const directId =
      card.dataset.productId ||
      card.getAttribute('data-product-id');

    if (directId) {
      return String(directId);
    }

    const productIdElement =
      card.querySelector('[data-product-id]');

    if (productIdElement instanceof HTMLElement) {
      return String(
        productIdElement.dataset.productId ||
        productIdElement.getAttribute('data-product-id') ||
        ''
      );
    }

    return '';
  }

  /**
   * Read the product path from a resource-card link.
   *
   * @param {HTMLElement} card
   * @returns {string}
   */
  function getResourceCardProductPath(card) {
    const productLink =
      card.querySelector('.resource-card__link') ||
      card.querySelector('a[href*="/products/"]');

    if (!(productLink instanceof HTMLAnchorElement)) {
      return '';
    }

    try {
      return new URL(
        productLink.href,
        window.location.origin
      ).pathname;
    } catch {
      return productLink.getAttribute('href') || '';
    }
  }

  /**
   * Determine whether a Header resource card represents the configured
   * Custom Fragrance Bundle.
   *
   * Product ID is checked first.
   * The product URL handle is used as the fallback.
   *
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  function isBundleResourceCard(card) {
    const resourceProductId =
      getResourceCardProductId(card);

    if (
      resourceProductId &&
      resourceProductId === config.bundleProductId
    ) {
      return true;
    }

    const expectedPath =
      `/products/${config.bundleProductHandle}`;

    const resourcePath =
      getResourceCardProductPath(card);

    return (
      resourcePath === expectedPath ||
      resourcePath.startsWith(`${expectedPath}?`) ||
      resourcePath.startsWith(`${expectedPath}/`)
    );
  }

  /**
   * Create the fixed bundle-card savings message.
   *
   * @returns {HTMLSpanElement}
   */
  function createBundleCardMessage() {
    const message = document.createElement('span');

    message.className =
      'bandholz-bundle-card-message';

    const accessibleLabel =
      document.createElement('span');

    accessibleLabel.className =
      'visually-hidden';

    accessibleLabel.textContent =
      'Pricing: ';

    message.appendChild(accessibleLabel);
    message.append(config.bundleCardMessage);

    return message;
  }

  /**
   * Identify the bundle product inside Header resource cards and replace its
   * native price display with the fixed Bandholz savings message.
   *
   * @param {Document|Element} root
   */
  function initializeHeaderBundleCard(root = document) {
    const resourceCards = [];

    if (
      root instanceof Element &&
      root.matches('.resource-card')
    ) {
      resourceCards.push(root);
    }

    if ('querySelectorAll' in root) {
      resourceCards.push(
        ...root.querySelectorAll('.resource-card')
      );
    }

    for (const card of resourceCards) {
      if (!(card instanceof HTMLElement)) {
        continue;
      }

      if (
        card.dataset.bandholzBundleCard === 'true'
      ) {
        continue;
      }

      if (!isBundleResourceCard(card)) {
        continue;
      }

      card.dataset.bandholzBundleCard = 'true';

      const content =
        card.querySelector('.resource-card__content') ||
        card;

      if (!(content instanceof HTMLElement)) {
        continue;
      }

      if (
        content.querySelector(
          '.bandholz-bundle-card-message'
        )
      ) {
        continue;
      }

      content.appendChild(
        createBundleCardMessage()
      );
    }
  }

  /* --------------------------------------------------------------------------
   * 4. Header account + cart pixel icons
   *
   * Keep Ritual's vendor-managed snippets/header-actions.liquid unchanged.
   * Instead, update only the artwork inside the SVG elements Ritual renders.
   *
   * Preserving the existing SVG elements keeps Ritual's:
   * • Shopify account slot/class behavior
   * • Header action sizing and spacing
   * • Cart drawer trigger and accessible labels
   * • Cart bubble placement, animation and SVG mask
   * • Current Header/theme icon colors
   *
   * Both Bandholz paths use fill="currentColor", so the account icon continues
   * to follow --color-account-icon and the cart continues to inherit the
   * Header action color selected by the active Ritual color scheme.
   *
   * -Jenny 9/23/26
   * -------------------------------------------------------------------------- */

  const BANDHOLZ_HEADER_ICON_PATHS = {
    account:
      'M6 22H4v-4h2v4Zm14 0h-2v-4h2v4ZM8 18H6v-2h2v2Zm10 0h-2v-2h2v2Zm-2-2H8v-2h8v2Zm-1-4H9v-2h6v2Zm-6-2H7V4h2v6Zm8 0h-2V4h2v6Zm-2-6H9V2h6v2Z',
    cart:
      'M9 22H6v-3h3v3Zm11 0h-3v-3h3v3Zm-2-4H8v-2h10v2ZM8 16H6v-4h2v4Zm12 0h-2v-4h2v4ZM6 6h16v6h-2V8H6v4H4V4H2V2h4v4Z'
  };

  /**
   * Replace only an existing Ritual SVG's artwork while keeping the SVG node
   * and all theme-owned classes, slots, dimensions and surrounding behavior.
   *
   * @param {SVGElement} svg
   * @param {'account'|'cart'} iconName
   */
  function applyBandholzHeaderIcon(
    svg,
    iconName
  ) {
    const pathData =
      BANDHOLZ_HEADER_ICON_PATHS[iconName];

    if (!pathData) {
      return;
    }

    const existingPath =
      svg.querySelector(':scope > path');

    if (
      svg.dataset.bandholzHeaderIcon === iconName &&
      svg.getAttribute('viewBox') === '0 0 24 24' &&
      svg.getAttribute('fill') === 'currentColor' &&
      svg.children.length === 1 &&
      existingPath?.getAttribute('d') === pathData
    ) {
      return;
    }

    const path = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );

    path.setAttribute('d', pathData);
    path.setAttribute('fill', 'currentColor');

    svg.replaceChildren(path);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'currentColor');
    svg.dataset.bandholzHeaderIcon = iconName;
  }

  /**
   * Apply the Bandholz pixel artwork to Ritual's rendered Header actions.
   *
   * Signed-in Shopify customer avatars are intentionally untouched; this only
   * changes the signed-out account icon supplied by header-actions.liquid.
   *
   * @param {Document|Element} root
   */
  function initializeHeaderActionIcons(
    root = document
  ) {
    const accountSelector = [
      '#header-component',
      'svg.account-button__icon'
    ].join(' ');

    const cartSelector = [
      '#header-component',
      '.header-actions__cart-icon',
      '.svg-wrapper',
      'svg'
    ].join(' ');

    const accountIcons = [];
    const cartIcons = [];

    if (root instanceof Element) {
      if (root.matches(accountSelector)) {
        accountIcons.push(root);
      }

      if (root.matches(cartSelector)) {
        cartIcons.push(root);
      }
    }

    if ('querySelectorAll' in root) {
      accountIcons.push(
        ...root.querySelectorAll(accountSelector)
      );

      cartIcons.push(
        ...root.querySelectorAll(cartSelector)
      );
    }

    for (const icon of accountIcons) {
      if (icon instanceof SVGElement) {
        applyBandholzHeaderIcon(
          icon,
          'account'
        );
      }
    }

    for (const icon of cartIcons) {
      if (icon instanceof SVGElement) {
        applyBandholzHeaderIcon(
          icon,
          'cart'
        );
      }
    }
  }

  /**
   * Restore native list semantics to Ritual's desktop Header navigation.
   *
   * Ritual applies role="presentation" to the <li> elements passed into its
   * overflow-list component. That removes their native listitem semantics and
   * causes automated accessibility checks to report invalid list children.
   *
   * The More trigger also places role="listitem" on its button. Removing that
   * role allows the parent <li> to remain the list item while the control keeps
   * its correct native button semantics.
   *
   * @param {Document|Element} root
   */
  function initializeHeaderNavigationSemantics(
    root = document
  ) {
    const listItemSelector = [
      'header-menu',
      '.menu-list__list-item[role="presentation"]'
    ].join(' ');

    const moreButtonSelector = [
      'header-menu',
      '.menu-list__list-item[slot="more"]',
      '> button[role="listitem"]'
    ].join(' ');

    const listItems = [];
    const moreButtons = [];

    if (root instanceof Element) {
      if (root.matches(listItemSelector)) {
        listItems.push(root);
      }

      if (root.matches(moreButtonSelector)) {
        moreButtons.push(root);
      }
    }

    if ('querySelectorAll' in root) {
      listItems.push(
        ...root.querySelectorAll(listItemSelector)
      );

      moreButtons.push(
        ...root.querySelectorAll(moreButtonSelector)
      );
    }

    for (const listItem of listItems) {
      if (!(listItem instanceof HTMLLIElement)) {
        continue;
      }

      listItem.removeAttribute('role');
    }

    for (const moreButton of moreButtons) {
      if (!(moreButton instanceof HTMLButtonElement)) {
        continue;
      }

      moreButton.removeAttribute('role');
    }
  }

  /**
   * Normalize text read from an existing visible or translated control label.
   *
   * @param {string|null|undefined} value
   * @returns {string}
   */
  function normalizeAccessibleText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Add an accessible name to Ritual's sticky Add to Cart button.
   *
   * On mobile, Ritual visually hides .add-to-cart-text__content with
   * display: none, which also removes that text from the accessibility tree.
   * The translated action text remains in the DOM, so it is reused as the
   * button's aria-label rather than duplicating a hard-coded English label.
   *
   * A native aria-label supplied by a future Ritual update is preserved.
   * Labels previously added by this enhancement are refreshed after variant
   * morphing so unavailable and sold-out states remain accurate.
   *
   * @param {Document|Element} root
   */
  function initializeStickyAddToCartAccessibleNames(
    root = document
  ) {
    const buttonSelector = [
      'sticky-add-to-cart',
      '.sticky-add-to-cart__button'
    ].join(' ');

    const buttons = [];

    if (
      root instanceof Element &&
      root.matches(buttonSelector)
    ) {
      buttons.push(root);
    }

    if ('querySelectorAll' in root) {
      buttons.push(
        ...root.querySelectorAll(buttonSelector)
      );
    }

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) {
        continue;
      }

      const currentAriaLabel =
        button.getAttribute('aria-label') || '';

      const previousBandholzLabel =
        button.dataset.bandholzAccessibleName || '';

      const labelWasAddedByBandholz =
        previousBandholzLabel !== '' &&
        currentAriaLabel === previousBandholzLabel;

      if (
        currentAriaLabel &&
        !labelWasAddedByBandholz
      ) {
        continue;
      }

      const primaryLabel = button.querySelector(
        '.add-to-cart-text__content > span:first-child'
      );

      const fallbackLabel = button.querySelector(
        '.add-to-cart-text__content'
      );

      const accessibleName = normalizeAccessibleText(
        primaryLabel?.textContent ||
        fallbackLabel?.textContent
      );

      if (!accessibleName) {
        continue;
      }

      button.setAttribute('aria-label', accessibleName);
      button.dataset.bandholzAccessibleName = accessibleName;
    }
  }

  /**
   * Repair invalid selection semantics in Judge.me's review media grid.
   *
   * Judge.me currently renders native buttons with aria-selected but without
   * a role that supports aria-selected. The buttons already have descriptive
   * "Go to item" labels and roving tabindex behavior, so they should remain
   * native buttons.
   *
   * The selected state is translated to aria-current="true" on the active
   * navigation button, then the unsupported aria-selected attribute is
   * removed. If Judge.me later supplies a supported role such as tab, this
   * enhancement leaves its native implementation untouched.
   *
   * @param {Document|Element} root
   */
  function initializeJudgeMeMediaGridSemantics(
    root = document
  ) {
    const buttonSelector =
      'button.jm-media-grid__item';

    const supportedSelectedRoles = new Set([
      'gridcell',
      'option',
      'row',
      'tab',
      'treeitem'
    ]);

    const buttons = [];

    if (
      root instanceof Element &&
      root.matches(buttonSelector)
    ) {
      buttons.push(root);
    }

    if ('querySelectorAll' in root) {
      buttons.push(
        ...root.querySelectorAll(buttonSelector)
      );
    }

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) {
        continue;
      }

      const explicitRole =
        button.getAttribute('role') || '';

      if (supportedSelectedRoles.has(explicitRole)) {
        if (
          button.dataset.bandholzJudgeMeCurrent === 'true'
        ) {
          button.removeAttribute('aria-current');
          delete button.dataset.bandholzJudgeMeCurrent;
        }

        continue;
      }

      if (!button.hasAttribute('aria-selected')) {
        continue;
      }

      const isSelected =
        button.getAttribute('aria-selected') === 'true';

      const currentWasAddedByBandholz =
        button.dataset.bandholzJudgeMeCurrent === 'true';

      if (isSelected) {
        if (
          !button.hasAttribute('aria-current') ||
          currentWasAddedByBandholz
        ) {
          button.setAttribute('aria-current', 'true');
          button.dataset.bandholzJudgeMeCurrent = 'true';
        }
      } else if (currentWasAddedByBandholz) {
        button.removeAttribute('aria-current');
        delete button.dataset.bandholzJudgeMeCurrent;
      }

      button.removeAttribute('aria-selected');
    }
  }

  /**
   * 9. Judge.me read-only pixel-heart rating states
   *
   * Judge.me's legacy preview badge already has CSS-readable .jdgm--on,
   * .jdgm--half and .jdgm--off classes. The newer Review Widget exposes an
   * accessible numeric label but not dependable state classes on its icons.
   *
   * Add only visual state data attributes to complete, read-only, five-icon
   * ratings. Do not replace Judge.me's elements, accessible names, links or
   * its interactive star inputs. Unknown markup remains native.
   *
   * Judge.me display rounding: .01–.25 rounds down, .26–.75 is a half,
   * .76–.99 rounds up (the exact score stays in Judge.me's aria-label).
   * This runs inside the existing Bandholz MutationObserver; no additional
   * document-wide observer or polling is needed.
   *
   * @param {Document|Element} root
   */
  function initializeJudgeMePixelHeartRatings(root = document) {
    const ratingSelector = '.jm-star-rating[role="img"]';
    const iconSelector = '.jm-star-rating__font-icon';
    const ratings = [];

    if (root instanceof Element && root.matches(ratingSelector)) {
      ratings.push(root);
    }

    if ('querySelectorAll' in root) {
      ratings.push(...root.querySelectorAll(ratingSelector));
    }

    for (const ratingElement of ratings) {
      const label = ratingElement.getAttribute('aria-label') || '';
      const match = label.match(
        /(\d+(?:[.,]\d+)?)\s+out\s+of\s+(\d+(?:[.,]\d+)?)/i
      );
      const icons = [...ratingElement.querySelectorAll(iconSelector)];

      // Avoid changing a localized/unknown label or partially hydrated row.
      if (!match || icons.length !== 5) {
        if (ratingElement.dataset.bandholzHeartRating === 'true') {
          delete ratingElement.dataset.bandholzHeartRating;
        }
        continue;
      }

      const score = Number(match[1].replace(',', '.'));
      const maximum = Number(match[2].replace(',', '.'));

      if (
        !Number.isFinite(score) ||
        !Number.isFinite(maximum) ||
        maximum <= 0 ||
        score < 0 ||
        score > maximum
      ) {
        if (ratingElement.dataset.bandholzHeartRating === 'true') {
          delete ratingElement.dataset.bandholzHeartRating;
        }
        continue;
      }

      const normalized = Math.min(5, (score / maximum) * 5);
      const wholeHearts = Math.floor(normalized);
      const fractionalHundredths = Math.round(
        (normalized - wholeHearts) * 100
      );
      const hasNextFull = fractionalHundredths >= 76;
      const hasHalf = fractionalHundredths >= 26 && !hasNextFull;

      icons.forEach((icon, index) => {
        const state = index < wholeHearts ||
          (index === wholeHearts && hasNextFull)
            ? 'full'
            : index === wholeHearts && hasHalf
              ? 'half'
              : 'empty';

        if (icon.dataset.bandholzHeartState !== state) {
          icon.dataset.bandholzHeartState = state;
        }
      });

      // Activate heart CSS only after all five states are assigned.
      if (ratingElement.dataset.bandholzHeartRating !== 'true') {
        ratingElement.dataset.bandholzHeartRating = 'true';
      }
    }
  }

  /**
   * Initialize all Bandholz storefront customizations.
   *
   * @param {Document|Element} root
   */
  function initializeBandholzCustomizations(
    root = document
  ) {
    initializeProductCardBadges(root);
    initializeHeaderBundleCard(root);
    initializeHeaderActionIcons(root);
    initializeHeaderNavigationSemantics(root);
    initializeStickyAddToCartAccessibleNames(root);
    initializeJudgeMeMediaGridSemantics(root);
    initializeJudgeMePixelHeartRatings(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeBandholzCustomizations();
      },
      { once: true }
    );
  } else {
    initializeBandholzCustomizations();
  }

  /**
   * Reinitialize sections loaded or refreshed by the Shopify theme editor.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (event.target instanceof Element) {
        initializeBandholzCustomizations(
          event.target
        );
      }
    }
  );

  /**
   * Product grids, filtering, search interfaces, Header menus, product
   * variant morphing and Judge.me widgets can insert, hydrate or reapply
   * relevant markup and attributes after the initial page load.
   */
  const relevantNodeSelector = [
    '.bandholz-product-card-customizations',
    '.resource-card',
    'header-actions',
    '#header-component svg.account-button__icon',
    '#header-component .header-actions__cart-icon .svg-wrapper svg',
    [
      'header-menu',
      '.menu-list__list-item[role="presentation"]'
    ].join(' '),
    [
      'header-menu',
      '.menu-list__list-item[slot="more"]',
      '> button[role="listitem"]'
    ].join(' '),
    [
      'sticky-add-to-cart',
      '.sticky-add-to-cart__button'
    ].join(' '),
    'button.jm-media-grid__item',
    '.jm-star-rating[role="img"]',
    '.jm-star-rating__font-icon'
  ].join(', ');

  const relevantDynamicAncestorSelector = [
    'header-actions',
    'sticky-add-to-cart',
    '.jm-media-grid',
    '.jm-star-rating[role="img"]'
  ].join(', ');

  const observer = new MutationObserver(
    (mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element
        ) {
          if (
            mutation.attributeName === 'role' &&
            mutation.target.matches(
              'header-menu .menu-list__list-item'
            )
          ) {
            initializeHeaderNavigationSemantics(
              mutation.target
            );

            continue;
          }

          if (
            (mutation.attributeName === 'aria-label' ||
              mutation.attributeName === 'role') &&
            mutation.target.matches('.jm-star-rating[role="img"]')
          ) {
            initializeJudgeMePixelHeartRatings(mutation.target);
            continue;
          }

          if (
            mutation.attributeName === 'aria-selected' &&
            mutation.target.matches(
              'button.jm-media-grid__item'
            )
          ) {
            initializeJudgeMeMediaGridSemantics(
              mutation.target
            );

            continue;
          }
        }

        for (const addedNode of mutation.addedNodes) {
          if (!(addedNode instanceof Element)) {
            continue;
          }

          const isRelevantNode =
            addedNode.matches(relevantNodeSelector);

          const containsRelevantNode =
            addedNode.querySelector(
              relevantNodeSelector
            );

          const relevantDynamicAncestor =
            addedNode.closest(
              relevantDynamicAncestorSelector
            );

          if (
            isRelevantNode ||
            containsRelevantNode ||
            relevantDynamicAncestor
          ) {
            initializeBandholzCustomizations(
              relevantDynamicAncestor || addedNode
            );
          }
        }
      }
    }
  );

  observer.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: ['role', 'aria-selected', 'aria-label'],
      childList: true,
      subtree: true
    }
  );
})();